import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import FullscreenRoundedIcon from "@mui/icons-material/FullscreenRounded";
import FullscreenExitRoundedIcon from "@mui/icons-material/FullscreenExitRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AdsClickRoundedIcon from "@mui/icons-material/AdsClickRounded";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import AutoAwesomeMotionRoundedIcon from "@mui/icons-material/AutoAwesomeMotionRounded";
import CanvasContainer from "../../../canvas/CanvasContainer.tsx";
import {
  getRenderableElements,
  getSlideDesign,
  resolveTransition,
} from "../../../canvas/design/slideDesign";
import { getTheme, ensureFontsForThemeIds } from "../../../canvas/design/themes";
import {
  isLowPowerDevice,
  prefersReducedMotion,
  runTransition,
} from "../../../canvas/design/transitions";
import { runMorph } from "../../../canvas/design/morph";
import { buildPlaybackPlan } from "../../../canvas/design/elementAnimations";
import SlideMiniPreview from "../SlideMiniPreview";
import "./cinema.css";

const IDLE_HIDE_MS = 2600;
const AUTOPLAY_OPTIONS = [4, 6, 10, 15];
const INTERACTIVE_SELECTOR =
  ".leaflet-container, iframe, video, audio, a, button, input, select, textarea, [data-interactive], .cine-player-bar";

const slideData = (slide) => (Array.isArray(slide?.content?.data) ? slide.content.data : []);

const formatClock = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

/**
 * Fullscreen cinematic presentation.
 * Keeps every visited slide mounted (interactive histology / 3D viewers keep
 * their state) and preloads the neighbours of the current slide.
 */
export default function CinematicPlayer({
  slides,
  startIndex = 0,
  onExit,
  stageWidth = 1280,
  stageHeight = 720,
  autoFullscreen = true,
}) {
  const total = slides?.length || 0;
  const rootRef = useRef(null);
  const stageRef = useRef(null);
  const layerRefs = useRef(new Map());
  const transitionRef = useRef(null);

  const [index, setIndex] = useState(() => Math.max(0, Math.min(startIndex, total - 1)));
  const [step, setStep] = useState(0);
  const [instant, setInstant] = useState(false);
  const [transition, setTransition] = useState(null);
  const [enterDelay, setEnterDelay] = useState(0);
  const [tokens, setTokens] = useState(() => ({ [startIndex]: 1 }));
  const [visited, setVisited] = useState(() => new Set([startIndex]));
  const [ended, setEnded] = useState(false);

  const [scale, setScale] = useState(1);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [autoplay, setAutoplay] = useState(false);
  const [autoplaySeconds, setAutoplaySeconds] = useState(6);
  const [transitionsOn, setTransitionsOn] = useState(true);
  const [laserOn, setLaserOn] = useState(false);
  const [laserPos, setLaserPos] = useState(null);
  const [zoomTool, setZoomTool] = useState(false);
  const [zoom, setZoom] = useState({ scale: 1, x: 0, y: 0 });
  const [startedAt] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());

  const lowPower = useMemo(() => isLowPowerDevice(), []);
  const reducedMotion = useMemo(() => prefersReducedMotion(), []);

  const plans = useMemo(
    () => (slides || []).map((slide) => buildPlaybackPlan(getRenderableElements(slideData(slide)))),
    [slides]
  );
  const designs = useMemo(() => (slides || []).map((slide) => getSlideDesign(slideData(slide))), [slides]);

  useEffect(() => {
    ensureFontsForThemeIds(designs.map((d) => d.themeId).filter(Boolean));
  }, [designs]);

  const stepsHere = plans[index]?.steps || 0;

  /* ----------------------------- Navigation ----------------------------- */

  const goTo = useCallback(
    (target, { direction, revealAll = false } = {}) => {
      if (target < 0 || target >= total) return;
      if (transitionRef.current) {
        transitionRef.current.finish();
        transitionRef.current = null;
      }
      setEnded(false);
      setTransition((prev) => {
        const from = prev ? prev.to : index;
        if (from === target) return null;
        // fromStep freezes the outgoing slide's builds while it animates away.
        return {
          from,
          to: target,
          fromStep: step,
          direction: direction ?? (target > from ? 1 : -1),
          id: Date.now(),
        };
      });
      setIndex(target);
      setStep(revealAll ? plans[target]?.steps || 0 : 0);
      setInstant(revealAll);
      setTokens((prev) => ({ ...prev, [target]: (prev[target] || 0) + 1 }));
      setVisited((prev) => (prev.has(target) ? prev : new Set(prev).add(target)));
      setZoom({ scale: 1, x: 0, y: 0 });
    },
    [index, plans, step, total]
  );

  const next = useCallback(() => {
    if (ended) {
      onExit?.(index);
      return;
    }
    if (step < stepsHere) {
      setInstant(false);
      setStep((s) => s + 1);
      return;
    }
    if (index < total - 1) {
      goTo(index + 1, { direction: 1 });
    } else {
      setEnded(true);
      setAutoplay(false);
    }
  }, [ended, goTo, index, onExit, step, stepsHere, total]);

  const prev = useCallback(() => {
    if (ended) {
      setEnded(false);
      return;
    }
    if (step > 0) {
      setStep((s) => s - 1);
      return;
    }
    if (index > 0) goTo(index - 1, { direction: -1, revealAll: true });
  }, [ended, goTo, index, step]);

  /* ---------------------------- Transitions ----------------------------- */

  useLayoutEffect(() => {
    if (!transition) return undefined;
    const outEl = layerRefs.current.get(transition.from);
    const inEl = layerRefs.current.get(transition.to);
    const design = designs[transition.to];
    const theme = getTheme(design?.themeId);
    const spec = resolveTransition(design, theme);
    const type = transitionsOn && !reducedMotion ? spec.type : "none";

    let controller = null;
    if (type === "morph") {
      controller = runMorph({
        outEl,
        inEl,
        fromData: slideData(slides[transition.from]),
        toData: slideData(slides[transition.to]),
        duration: spec.duration,
        easing: spec.easing,
      });
    }
    if (!controller) {
      controller = runTransition({
        outEl,
        inEl,
        stageEl: stageRef.current,
        type: type === "morph" ? "fade" : type,
        duration: spec.duration,
        easing: spec.easing,
        direction: transition.direction,
        width: stageWidth,
        height: stageHeight,
        lowPower,
      });
    }
    setEnterDelay(type === "none" ? 0 : Math.round(spec.duration * 0.6));
    transitionRef.current = controller;
    let cancelled = false;
    controller.finished.then(() => {
      if (cancelled) return;
      transitionRef.current = null;
      setTransition((current) => (current && current.id === transition.id ? null : current));
    });
    return () => {
      cancelled = true;
    };
  }, [transition?.id]);

  /* ------------------------------ Autoplay ------------------------------ */

  useEffect(() => {
    if (!autoplay) return undefined;
    const timer = window.setTimeout(next, autoplaySeconds * 1000);
    return () => window.clearTimeout(timer);
  }, [autoplay, autoplaySeconds, next, index, step]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  /* ------------------------------- Scaling ------------------------------ */

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setScale(Math.min(w / stageWidth, h / stageHeight));
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [stageHeight, stageWidth]);

  /* ----------------------------- Fullscreen ----------------------------- */

  const toggleFullscreen = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {});
    } else {
      el.requestFullscreen?.().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    if (autoFullscreen && rootRef.current?.requestFullscreen && !document.fullscreenElement) {
      rootRef.current.requestFullscreen().catch(() => {});
    }
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    };
  }, []);

  const exit = useCallback(() => {
    if (transitionRef.current) transitionRef.current.finish();
    onExit?.(index);
  }, [index, onExit]);

  /* ------------------------------ Keyboard ------------------------------ */

  useEffect(() => {
    const onKey = (e) => {
      const target = e.target;
      const typing =
        target &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);
      if (typing) return;
      const inMap = target?.closest?.(".leaflet-container");
      const key = e.key;
      if (key === "Escape") {
        if (zoom.scale > 1) {
          setZoom({ scale: 1, x: 0, y: 0 });
          return;
        }
        e.preventDefault();
        exit();
        return;
      }
      if (inMap && key.startsWith("Arrow")) return;
      if (["ArrowRight", "ArrowDown", "PageDown", " ", "Enter"].includes(key)) {
        e.preventDefault();
        next();
      } else if (["ArrowLeft", "ArrowUp", "PageUp", "Backspace"].includes(key)) {
        e.preventDefault();
        prev();
      } else if (key === "Home") {
        e.preventDefault();
        goTo(0, { direction: -1 });
      } else if (key === "End") {
        e.preventDefault();
        goTo(total - 1, { direction: 1 });
      } else if (key === "f" || key === "F") {
        toggleFullscreen();
      } else if (key === "l" || key === "L") {
        setLaserOn((v) => !v);
      } else if (key === "z" || key === "Z") {
        setZoomTool((v) => !v);
      } else if (key === "a" || key === "A") {
        setAutoplay((v) => !v);
      } else if (key === "t" || key === "T") {
        setTransitionsOn((v) => !v);
      } else if (key === "+" || key === "=") {
        setZoom((z) => ({ ...z, scale: Math.min(4, z.scale * 1.25) }));
      } else if (key === "-") {
        setZoom((z) => (z.scale / 1.25 <= 1.01 ? { scale: 1, x: 0, y: 0 } : { ...z, scale: z.scale / 1.25 }));
      } else if (key === "0") {
        setZoom({ scale: 1, x: 0, y: 0 });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [exit, goTo, next, prev, toggleFullscreen, total, zoom.scale]);

  /* ------------------------- Idle-hide controls ------------------------- */

  useEffect(() => {
    let timer = null;
    const wake = () => {
      setControlsVisible(true);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setControlsVisible(false), IDLE_HIDE_MS);
    };
    wake();
    window.addEventListener("mousemove", wake);
    window.addEventListener("touchstart", wake, { passive: true });
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("mousemove", wake);
      window.removeEventListener("touchstart", wake);
    };
  }, []);

  /* --------------------------- Laser + zoom/pan ------------------------- */

  const onPointerMove = (e) => {
    if (laserOn) {
      const rect = rootRef.current?.getBoundingClientRect();
      if (rect) setLaserPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    }
  };

  const panRef = useRef(null);
  const onZoomPointerDown = (e) => {
    panRef.current = { x: e.clientX, y: e.clientY, start: { ...zoom }, moved: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onZoomPointerMove = (e) => {
    const pan = panRef.current;
    if (!pan) return;
    const dx = e.clientX - pan.x;
    const dy = e.clientY - pan.y;
    if (Math.abs(dx) + Math.abs(dy) > 4) pan.moved = true;
    if (pan.moved && pan.start.scale > 1) {
      setZoom({ ...pan.start, x: pan.start.x + dx, y: pan.start.y + dy });
    }
  };
  const onZoomPointerUp = (e) => {
    const pan = panRef.current;
    panRef.current = null;
    if (!pan || pan.moved) return;
    // Click zooms toward the point; click while zoomed in zooms back out.
    const rect = stageRef.current?.parentElement?.getBoundingClientRect();
    if (!rect) return;
    if (zoom.scale > 1) {
      setZoom({ scale: 1, x: 0, y: 0 });
      return;
    }
    const cx = e.clientX - (rect.left + rect.width / 2);
    const cy = e.clientY - (rect.top + rect.height / 2);
    const s = 2.2;
    setZoom({ scale: s, x: -cx * (s - 1), y: -cy * (s - 1) });
  };

  const onStageClick = (e) => {
    if (zoomTool) return;
    if (e.target?.closest?.(INTERACTIVE_SELECTOR)) return;
    next();
  };

  /* ------------------------------- Render ------------------------------- */

  const mounted = useMemo(() => {
    const keep = new Set(visited);
    [index - 1, index, index + 1, index + 2].forEach((i) => {
      if (i >= 0 && i < total) keep.add(i);
    });
    return keep;
  }, [index, total, visited]);

  const progress = total ? (index + (stepsHere ? step / (stepsHere + 1) : 0) + (ended ? 1 : 0)) / total : 0;
  const nextSlide = index < total - 1 ? slides[index + 1] : null;

  return (
    <div
      ref={rootRef}
      className={`cine-player${controlsVisible ? "" : " is-idle"}${laserOn ? " is-laser" : ""}${zoomTool ? " is-zoom" : ""}`}
      onMouseMove={onPointerMove}
      onMouseLeave={() => setLaserPos(null)}
      role="dialog"
      aria-modal="true"
      aria-label="Presentation"
    >
      <div className="cine-player-viewport" onClick={onStageClick}>
        <div
          className="cine-player-zoom"
          style={{
            transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.scale})`,
            transition: panRef.current ? "none" : "transform 420ms cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        >
          <div
            className="cine-player-stage"
            style={{ width: stageWidth * scale, height: stageHeight * scale }}
          >
            <div
              ref={stageRef}
              className="cine-player-stage-inner"
              style={{
                width: stageWidth,
                height: stageHeight,
                transform: `scale(${scale})`,
              }}
            >
              {(slides || []).map((slide, i) => {
                if (!mounted.has(i)) return null;
                const isCurrent = i === index;
                const isLeaving = transition && i === transition.from && !isCurrent;
                const visible = isCurrent || isLeaving;
                const plan = plans[i];
                return (
                  <div
                    key={slide?.id ?? i}
                    ref={(node) => {
                      if (node) layerRefs.current.set(i, node);
                      else layerRefs.current.delete(i);
                    }}
                    className="cine-player-layer"
                    style={{
                      visibility: visible ? "visible" : "hidden",
                      pointerEvents: isCurrent ? "auto" : "none",
                      zIndex: isCurrent ? 1 : 0,
                    }}
                    aria-hidden={!isCurrent}
                  >
                    <CanvasContainer
                      initialData={slideData(slide)}
                      isPlayMode
                      clipStage
                      stageWidth={stageWidth}
                      stageHeight={stageHeight}
                      stillBackdrop={lowPower || !visible}
                      playback={{
                        // A leaving slide stays "active" at its last step until the
                        // transition ends, so nothing on it snaps back mid-animation.
                        active: isCurrent || Boolean(isLeaving),
                        step: isCurrent ? step : isLeaving ? transition.fromStep : 0,
                        instant: isCurrent ? instant || reducedMotion : true,
                        enterDelay: isCurrent ? enterDelay : 0,
                        token: tokens[i] || 0,
                        schedule: plan?.schedule,
                        focusStart: plan?.focusStart,
                      }}
                    />
                  </div>
                );
              })}
              {ended ? (
                <div className="cine-player-end" onClick={(e) => e.stopPropagation()}>
                  <p>End of presentation</p>
                  <button type="button" onClick={exit}>
                    Exit
                  </button>
                  <button type="button" className="is-ghost" onClick={() => goTo(0, { direction: -1 })}>
                    Start over
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
        {zoomTool ? (
          <div
            className="cine-player-zoom-capture"
            onPointerDown={onZoomPointerDown}
            onPointerMove={onZoomPointerMove}
            onPointerUp={onZoomPointerUp}
            onClick={(e) => e.stopPropagation()}
            title="Click to zoom, drag to pan, click again to reset"
          />
        ) : null}
      </div>

      {laserOn && laserPos ? (
        <span className="cine-laser" style={{ left: laserPos.x, top: laserPos.y }} aria-hidden="true" />
      ) : null}

      <div className="cine-player-progress" aria-hidden="true">
        <span style={{ transform: `scaleX(${Math.min(1, progress)})` }} />
      </div>

      <div className="cine-player-bar" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="cine-btn" onClick={prev} aria-label="Previous" disabled={index === 0 && step === 0}>
          <ArrowBackIosNewRoundedIcon fontSize="inherit" />
        </button>
        <span className="cine-player-count" aria-live="polite">
          <strong>{index + 1}</strong> / {total}
          {stepsHere ? <em>{` · build ${step}/${stepsHere}`}</em> : null}
        </span>
        <button type="button" className="cine-btn" onClick={next} aria-label="Next">
          <ArrowForwardIosRoundedIcon fontSize="inherit" />
        </button>
        <span className="cine-sep" />
        <button
          type="button"
          className={`cine-btn${autoplay ? " is-on" : ""}`}
          onClick={() => setAutoplay((v) => !v)}
          aria-label={autoplay ? "Pause autoplay" : "Start autoplay"}
          aria-pressed={autoplay}
          title="Autoplay (A)"
        >
          {autoplay ? <PauseRoundedIcon fontSize="inherit" /> : <PlayArrowRoundedIcon fontSize="inherit" />}
        </button>
        <select
          className="cine-select"
          value={autoplaySeconds}
          onChange={(e) => setAutoplaySeconds(Number(e.target.value))}
          aria-label="Autoplay interval"
        >
          {AUTOPLAY_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}s
            </option>
          ))}
        </select>
        <button
          type="button"
          className={`cine-btn${laserOn ? " is-on" : ""}`}
          onClick={() => setLaserOn((v) => !v)}
          aria-label="Laser pointer"
          aria-pressed={laserOn}
          title="Laser pointer (L)"
        >
          <AdsClickRoundedIcon fontSize="inherit" />
        </button>
        <button
          type="button"
          className={`cine-btn${zoomTool ? " is-on" : ""}`}
          onClick={() => {
            setZoomTool((v) => !v);
            setZoom({ scale: 1, x: 0, y: 0 });
          }}
          aria-label="Zoom and pan"
          aria-pressed={zoomTool}
          title="Zoom & pan (Z, +/−, 0)"
        >
          <ZoomInRoundedIcon fontSize="inherit" />
        </button>
        <button
          type="button"
          className={`cine-btn${transitionsOn ? " is-on" : ""}`}
          onClick={() => setTransitionsOn((v) => !v)}
          aria-label="Transitions"
          aria-pressed={transitionsOn}
          title="Slide transitions (T)"
        >
          <AutoAwesomeMotionRoundedIcon fontSize="inherit" />
        </button>
        <button
          type="button"
          className="cine-btn"
          onClick={toggleFullscreen}
          aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
          title="Fullscreen (F)"
        >
          {isFullscreen ? <FullscreenExitRoundedIcon fontSize="inherit" /> : <FullscreenRoundedIcon fontSize="inherit" />}
        </button>
        <span className="cine-sep" />
        <span className="cine-player-clock" title="Elapsed time">
          {formatClock(now - startedAt)}
        </span>
        {nextSlide ? (
          <span className="cine-player-next" title="Next slide">
            <span className="cine-player-next-label">Next</span>
            <span className="cine-player-next-thumb">
              {nextSlide.thumbnail ? (
                <img src={nextSlide.thumbnail} alt="" />
              ) : (
                <SlideMiniPreview elements={getRenderableElements(slideData(nextSlide))} />
              )}
            </span>
          </span>
        ) : null}
        <button type="button" className="cine-btn" onClick={exit} aria-label="Exit presentation" title="Exit (Esc)">
          <CloseRoundedIcon fontSize="inherit" />
        </button>
      </div>
    </div>
  );
}
